import {
  SearchClientsByEmailOptions,
  SearchClientsOptions,
} from '@/components/select/SelectClient'
import { CLIENT_PARTY_TYPE } from '@/lib/constants'
import { API_PATH_INTERNAL } from '@/lib/constants'
import { getFirstAndLastName } from '@/lib/helpers/helperFunctions'
import apiService from '@/lib/services/apiService'
import { ClientPartyType } from '@/types/misc'
import {
  BulkDeleteClientsRequest,
  CreateClientForReferralRequest,
  CreateClientPaymentRequest,
  CreateClientRequest,
  CreateReferrerClientRequest,
  GetClientReferralsParams,
  GetClientsParams,
  GetCompassClientReportsParams,
  RequestClientCompassUpdatesRequest,
  RequestMergeClientRequest,
  UpdateClientForReferralRequest,
  UpdateClientRequest,
  UpdateCompanyClientRequest,
  ValidatePhoneNumberRequest,
} from '@/types/requests/clients'
import { ClientMetadataItem } from '@/types/responses/client-metadata'
import {
  BulkDeleteClientsResponse,
  ClientCollection,
  ClientItem,
  ClientPaymentItem,
  ClientReferralCollection,
  ClientReferralFormMetadataItem,
  CreateReferrerClientResponse,
  IntegrationLinkedClientItem,
  RequestMergeClientResponse,
  UpdateCompanyClientResponse,
  ValidatePhoneNumberResponse,
} from '@/types/responses/clients'
import { CompassReportCollection } from '@/types/responses/compass-reports'
import { TokenResponse } from '@/types/responses/tokens'

class ClientService {
  private endpoint = `${API_PATH_INTERNAL}/clients`

  getIri(clientId: string | number): IRI {
    return `${this.endpoint}/${clientId}`
  }

  async getClient(clientId: number): Promise<ClientItem> {
    const response = await apiService.get(`${this.endpoint}/${clientId}`)

    return response.data
  }

  async getClientFromIntegration(
    companyIntegrationId: number,
    integrationClientId: string,
  ): Promise<IntegrationLinkedClientItem> {
    const response = await apiService.get(
      `${API_PATH_INTERNAL}/company-integrations/${companyIntegrationId}/integration-clients/${integrationClientId}/clients`,
    )

    return response.data
  }

  async createClient(request: CreateClientRequest) {
    const response = await apiService.post(this.endpoint, request)

    return response.data
  }

  async createClientForReferral(
    request: CreateClientForReferralRequest,
  ): Promise<ClientItem> {
    const response = await apiService.post(this.endpoint, request)

    return response.data
  }

  // @todo: Create a dedicated API endpoint to encapsulate this logic
  async createClientForReferralOrFindExisting(
    companyId: number,
    request: CreateClientForReferralRequest,
  ): Promise<ClientItem> {
    let newClient: ClientItem | null = null
    let newClientError: unknown

    try {
      newClient = await this.createClientForReferral(request)
    } catch (error) {
      newClientError = error
      // there is error creating client,
      // most likely it is already created by other row, so try to find it
      const alreadyCreatedClients = await this.searchClientsByEmail({
        companyId: companyId,
        email: request.email,
      })

      if (
        alreadyCreatedClients['hydra:member'].length === 1 &&
        alreadyCreatedClients['hydra:member'][0]
      ) {
        newClient = await this.getClient(
          alreadyCreatedClients['hydra:member'][0].id,
        )
      }
    }

    if (!newClient) {
      throw newClientError
    }

    return newClient
  }

  async updateClientForReferral(
    clientId: number,
    request: UpdateClientForReferralRequest,
  ) {
    const response = await apiService.patch(
      `${this.endpoint}/${clientId}`,
      request,
    )

    return response.data
  }

  async searchClients(
    options: SearchClientsOptions,
  ): Promise<ClientCollection> {
    const response = await apiService.get(this.endpoint, {
      params: {
        'company.id': options.companyId,
        'order[firstName]': 'asc',
        'order[lastName]': 'asc',
        fullName: options.clientName,
      },
    })

    return response.data
  }

  async searchClientsByEmail(
    options: SearchClientsByEmailOptions,
  ): Promise<ClientCollection> {
    const response = await apiService.get(this.endpoint, {
      params: {
        'company.id': options.companyId,
        email: options.email,
      },
    })

    return response.data
  }

  async getClients(params?: GetClientsParams): Promise<ClientCollection> {
    const response = await apiService.get(`${API_PATH_INTERNAL}/clients`, {
      params,
    })

    return response.data
  }

  async getClientReferrals(
    params?: GetClientReferralsParams,
  ): Promise<ClientReferralCollection> {
    const response = await apiService.get(`${this.endpoint}/referrals`, {
      params,
    })

    return response.data
  }

  async getClientReferralFormMetadata(
    clientReferralCode: string,
  ): Promise<ClientReferralFormMetadataItem> {
    const response = await apiService.get(
      `${this.endpoint}/${clientReferralCode}/client-referral-form-metadata`,
    )

    return response.data
  }

  async createReferrerClient(
    request: CreateReferrerClientRequest,
  ): Promise<CreateReferrerClientResponse> {
    const response = await apiService.post(
      `${this.endpoint}/create-referrer-client`,
      request,
    )

    return response.data
  }

  async getClientCompassReports(
    params: GetCompassClientReportsParams,
  ): Promise<CompassReportCollection> {
    const response = await apiService.get(
      `${API_PATH_INTERNAL}/compass-reports`,
      {
        params,
      },
    )

    return response.data
  }

  async createClientMetadata(): Promise<ClientMetadataItem> {
    const response = await apiService.post(
      `${API_PATH_INTERNAL}/client-metadata`,
      {},
    )

    return response.data
  }

  async createOrUpdateClientViaCompass(
    request: UpdateCompanyClientRequest,
  ): Promise<TokenResponse> {
    const response = await apiService.post(
      `${this.endpoint}/create-or-update-via-compass`,
      request,
    )

    return response.data
  }

  async updateCompanyClient(
    clientIri: IRI,
    request: UpdateCompanyClientRequest,
  ): Promise<UpdateCompanyClientResponse> {
    const response = await apiService.patch(clientIri, request)

    return response.data
  }

  async updateClient(
    clientIri: IRI,
    request: UpdateClientRequest,
  ): Promise<UpdateCompanyClientResponse> {
    const response = await apiService.patch(clientIri, request)

    return response.data
  }

  async deleteClients(
    request: BulkDeleteClientsRequest,
  ): Promise<BulkDeleteClientsResponse> {
    const response = await apiService.post(
      `${this.endpoint}/bulk-delete`,
      request,
    )

    return response.data
  }

  async requestCompassUpdates(
    request: RequestClientCompassUpdatesRequest,
  ): Promise<unknown> {
    const response = await apiService.post(
      `${this.endpoint}/request-updates`,
      request,
    )

    return response.data
  }

  async requestClientMerge(
    request: RequestMergeClientRequest,
  ): Promise<RequestMergeClientResponse> {
    const response = await apiService.post(
      `${this.endpoint}/request-merge-clients`,
      request,
    )

    return response.data
  }

  async getClientPaymentByToken(
    clientId: number,
    token: string,
  ): Promise<ClientPaymentItem> {
    const response = await apiService.get<ClientPaymentItem>(
      `${this.endpoint}/${clientId}/payment`,
      { params: { token } },
    )

    return response.data
  }

  async submitPaymentDetailsForClient(
    clientId: number,
    token: string,
    request: CreateClientPaymentRequest,
  ): Promise<ClientPaymentItem> {
    const response = await apiService.patch<ClientPaymentItem>(
      `${this.endpoint}/${clientId}/payment`,
      request,
      { params: { token } },
    )

    return response.data
  }

  async validatePhoneNumber(
    request: ValidatePhoneNumberRequest,
  ): Promise<ValidatePhoneNumberResponse> {
    const response = await apiService.post(
      `${this.endpoint}/validate-phone-number`,
      request,
    )

    return response.data
  }

  getClientTypeLabel(clientType: ClientPartyType): string {
    const labels: Record<ClientPartyType, string> = {
      PERSON: 'Individual',
      CORPORATE: 'Business',
      TRUST: 'Trust',
      JOINT: 'Joint',
    }

    return labels[clientType]
  }

  isBusinessOrTrust(clientType: ClientPartyType) {
    return [CLIENT_PARTY_TYPE.CORPORATE, CLIENT_PARTY_TYPE.TRUST].includes(
      clientType,
    )
  }

  getAbbreviatedName(fullName: string): string {
    const { firstName, lastName } = getFirstAndLastName(fullName)

    return lastName ? `${firstName} ${lastName.charAt(0)}.` : firstName || ''
  }

  getAbbreviatedNameAndPartyName({
    fullName,
    partyName,
  }: {
    fullName: string
    partyName?: string
  }): string {
    const abbreviatedName = this.getAbbreviatedName(fullName)

    return partyName ? `${abbreviatedName} (${partyName})` : abbreviatedName
  }
}

const clientService = new ClientService()

export default clientService
